/* Builder A · WF-11 seam types + hooks (ruling 30/08: builder hooks live in
   the builder’s own folder). The mail *bodies* are not modelled here — they
   come from '@/emails', read-only; these types describe the screens that
   manage delivery, not the messages themselves. */
import { useQuery } from '@tanstack/react-query'

/* ---------- EM-07 · notification centre ----------
   RETIRED as a source (lib pack 3, 08/10): the centre and the bell read contract 11's feed through
   `useNotifications` in @/lib/api. These two types stay only because mocks/data-a still builds the old
   fixture with them; they go with that fixture. */
export interface NotifEvent {
  id: string; title: string; meta: string
  action: string
  /** security / billing / account — cannot be turned off, and says so. */
  mandatory: boolean
  unread: boolean
}
export interface NotifFeed { events: NotifEvent[] }

/* ---------- EM-02 · template library ---------- */
export type TemplateStatus = 'publiee' | 'brouillon' | 'revue'
export interface TemplateRow {
  id: string; name: string; classeLabel: string; langue: string
  version: string; status: TemplateStatus; statusLabel: string
  lastSend: string
}
export interface TemplateLibrary {
  sender: string
  rows: TemplateRow[]
}

/* EM-08 · the préférences matrix now lives on the seam: NotificationPreferences in @/lib/api (one path,
   /v1/app/messages/preferences — nine-answers item 7, 08/10). */

/* ---------- EM-09 · digests & delivery log ---------- */
export type SendState = 'delivered' | 'suppressed' | 'bounced'
export interface SendRow {
  id: string; label: string; classe: string
  state: SendState; stateLabel: string
}
export interface Delivery {
  digestWhen: string; digestTo: string; digestContent: string
  sends: SendRow[]
}

/* ---------- transport (mirrors lib/api.ts) ---------- */
async function get<T>(path: string): Promise<T> {
  const res = await fetch(`/v1${path}`)
  if (!res.ok) throw new Error(`${res.status} on ${path}`)
  return res.json() as Promise<T>
}

export const useTemplates = () =>
  useQuery({ queryKey: ['messages', 'templates'], queryFn: () => get<TemplateLibrary>('/messages/templates') })
export const useDelivery = () =>
  useQuery({ queryKey: ['messages', 'delivery'], queryFn: () => get<Delivery>('/messages/delivery') })

/* ================= WF-12 · AI review responses (AI-01…AI-04) ================= */

export type ReviewState = 'draft-ready' | 'human-review' | 'answered'
export interface Review {
  id: string; stars: number; quote: string; author: string; meta: string
  state: ReviewState; stateLabel: string
}
export interface ReviewInbox {
  location: string
  /** the honest connection state — never a fake feed (L7) */
  googleConnected: boolean
  items: Review[]
}

export interface Voice {
  tones: string[]; tone: string
  vouvoiement: boolean
  description: string
  habits: string
}

export interface Draft {
  reviewLabel: string
  body: string
  signature: string
}

export interface HealthItem {
  id: string; title: string; meta: string
  kind: 'retry' | 'action' | 'ignored'
  label: string
}

export const useReviews = () =>
  useQuery({ queryKey: ['reviews'], queryFn: () => get<ReviewInbox>('/reviews') })
export const useVoice = () =>
  useQuery({ queryKey: ['reviews', 'voice'], queryFn: () => get<Voice>('/reviews/voice') })
export const useDraft = () =>
  useQuery({ queryKey: ['reviews', 'draft'], queryFn: () => get<Draft>('/reviews/draft') })
export const useReviewHealth = () =>
  useQuery({ queryKey: ['reviews', 'health'], queryFn: () => get<HealthItem[]>('/reviews/health') })
